import React, { useEffect, useState, useRef } from "react";
import ChatItem from "./chat-item/ChatItem";
import ProfileDropdown from "./ProfileDropdown/ProfileDropdown";
import { connect } from "react-redux";
import { IChatPanelProps } from "./chats-panel-main/chat-item-list/~types/ChatPanelProps";
import "./ChatsPanel.css";
import { IApplicationState } from "src/~store/models/ApplicationState";

import {
    GetOpenedOperatorChatsAction,
    ChangeOperatorStatusAction,
    UpdateIncomingChatInStateAction,
    AddNewMessageToStateAction,
    GetOperatorInfoAction,
    GetChannelInfoAction,
    GetOperatorChannelsAction,
    IncreaseChatUnreadMessagesCountAction,
    UpdateMessageInStateAction,
    GetOperatorStatusListAction,
    UpdateOperatorStatusInStateAction,
    ChangeReconnectingStatusAction,
    GetAvailableContactTagsAction,
} from "./~store/actions/interfaces";
import { AppThunkAction } from "src/~store/models/AppThunkAction";
import {
    getChats,
    changeOperatorStatus,
    changeReconnectingStatus,
    addNewMessageToState,
    getOperatorInfo,
    getChannelInfo,
    getOperatorChannels,
    increaseChatUnreadMessagesCount,
    setChatVisibilityToState,
    updateChatMessage,
    UpdateIncomingChatInState,
    updateChatsInChatPanel,
    getOperatorStatusList,
    updateOperatorStatusInState,
    playNewMessageSound,
    onLoggedOut,
    pushNewMessageNotification,
    getAvailableContactTags,
    updateMessagePreviewToState,
    startOperatorLostConnectionHandler,
    updateIsContactOnline,
    loadChatToWidget,
    updateChatHistory,
} from "./~store/actions/actions";
import {
    IOperatorStatusViewModel,
    OperatorStatusType,
} from "./~store/state/ChatItemsState";
import { IChatItem } from "./~store/models/ChatItem";
import { IChat } from "../chats/~store/models/Chat";
import { hideChats, updateChats } from "../chats/~store/actions/actions";
import { DEFAULT_OPERATOR_AVATAR_ID } from "../operator/~store/models/ModelsConstants";
import {
    selectedChatIdSelector,
    chatItemsSelector,
    operatorStatusSelector,
    hubConnectionSelector,
    operatorNameSelector,
    operatorAvatarIdSelector,
    operatorIdSelector,
    operatorChannelsSelector,
    operatorsStatusListSelector,
    reconnectingStatusSelector,
    messegesSelector,
} from "./~store/selectors";
import { onGetChatsModeChanged } from "../chats/~store/actions/actions";
import { GetChatsMode } from "../chats/~store/models/enums/GetChatsMode";
import { IMessageItem, MessageType } from "./~store/models/MessageItem";
import Loader from "../shared/components/loader/Loader";
import { IMessagePreview } from "./~store/models/MessagePreview";
import { FIRST_PAUSE_CATEGORY_ID } from "./~store/actions/ChatConstants";
import { startSignalRConnection } from "src/~signalR/SignalRHub";
import { useTranslation } from "react-i18next";
import { Avatar } from "@mui/material";
import ContactIcon from "@mui/icons-material/AccountCircle";
import { GET_FILE_BY_ID_API_ROUTE } from "./~api/apiRoutes";
import { pauseCategoriesSelector } from "../setting/~store/selector";
import { redirectToLogin } from "src/~store/history";
import { BASE_URL } from "../AppConstants";
import { NOTIFICATION_SOUND_URL } from "src/app/shared/helpers/notification-helper/NotificationHelperConstants";

type ReduxType = ReturnType<typeof mapStateToProps> & IChatPanelProps;

let selectedChatId: number = 0;
let selectedChatMessages: IMessageItem[] = [];
let newMessageSoundPlayer: HTMLAudioElement;
let offlineHandled: boolean = false;

const ChatsPanel = (props: ReduxType) => {
    const { t } = useTranslation();

    const isPreviewActiveRef = useRef(false);
    const operatorIdRef = useRef(props.operatorId);

    useEffect(() => {
        startSignalR();
        props.hubConnection.onreconnecting(() => {
            props.changeReconnectingStatus(true);
            props.startOperatorLostConnectionHandler();
        });
        props.hubConnection.onreconnected(async () => {
            props.changeReconnectingStatus(false);
            onOperatorStatus(
                await props.hubConnection.invoke("CheckOperatorStatus")
            );
        });
        props.getOperatorInfo();
        props.getOperatorChannels();
        props.getOperatorStatusList();
        props.getAvailableContactTags();
        newMessageSoundPlayer = new Audio(BASE_URL + NOTIFICATION_SOUND_URL)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        props.getChats();
    }, []);

    useEffect(() => {
        selectedChatId = props.selectedChatId;
    }, [props.selectedChatId]);
    useEffect(() => {
        selectedChatMessages = props.selectedChatMessages;
    }, [props.selectedChatMessages]);
    useEffect(() => {
        operatorIdRef.current = props.operatorId;
    }, [props.operatorId]);

    const userInStorage = sessionStorage.getItem("user");
    const customerId =
        userInStorage === null
            ? ""
            : JSON.parse(userInStorage.toString()).customerId;
    const operatorAvatarUrl =
        props.operatorAvatarId !== DEFAULT_OPERATOR_AVATAR_ID
            ? GET_FILE_BY_ID_API_ROUTE +
              props.operatorAvatarId +
              "&customerId=" +
              customerId
            : "";

    const onOperatorStatusChanged = (status: OperatorStatusType) => {
        switch (status) {
            case OperatorStatusType.Online:
            case OperatorStatusType.Pause:
            case OperatorStatusType.Pause1:
            case OperatorStatusType.Pause2:
            case OperatorStatusType.Pause3:
            case OperatorStatusType.Pause4:
            case OperatorStatusType.Pause5:
            case OperatorStatusType.Pause6:
            case OperatorStatusType.Pause7:
            case OperatorStatusType.Pause8:
            case OperatorStatusType.Pause9:
            case OperatorStatusType.Pause10:
                props.getChats();
                return;
            case OperatorStatusType.Offline:
                if (!offlineHandled) {
                    props.getChats();
                }
                offlineHandled = false;
                return;
        }
    };

    const startSignalR = async () => {
        props.hubConnection.on("UpdateChat", onUpdateChat);
        props.hubConnection.on("UpdateChats", onUpdateChats);
        props.hubConnection.on("HideChats", onHideChats);
        props.hubConnection.on("NewMessage", onNewMessage);
        props.hubConnection.on("MessagePreview", onMessagePreview);
        props.hubConnection.on("UpdateOperatorStatus", onUpdateOperatorStatus);
        props.hubConnection.on("UpdateMessage", onUpdateMessage);
        props.hubConnection.on("LogoutOperator", onLogoutOperator);
        props.hubConnection.on("ClientWentOffline", onClientWentOffline);
        props.hubConnection.on("ClientWentOnline", onClientWentOnline);

        onOperatorStatus(await startSignalRConnection(props.hubConnection));
    };

    const onUpdateChat = (chat: IChatItem, additionalData: any) => {
        if (additionalData?.chatFromHistory === true) {
            chat.fromHistory = true;
        } else {
            chat.fromHistory = false;
        }
        props.UpdateIncomingChatInState(chat);
    };

    const onUpdateChats = (chats: any[]) => {
        props.updateChatsInChatPanel(chats);
        props.updateChats(chats);
        props.updateChatHistory(chats);
    };

    const onHideChats = (chatsIds: any[]) => {
        props.hideChats(chatsIds);
        if (chatsIds.includes(selectedChatId))
        {
            props.setChatVisibility(false);
            props.setChatVisibilityToState(false);
        }
    }

    const onNewMessage = (message: IMessageItem) => {
        if(selectedChatId === message.chatId || !!selectedChatMessages.find(m => m.chatId === message.chatId)) {
            props.addNewMessageToState(message);

            if (message.messageType === MessageType.clientMessage || message.messageType === MessageType.operatorMessage) {
                const isMyOwnMessage = message.messageType === MessageType.operatorMessage && message.operatorId === operatorIdRef.current;

                _scrollDown(isMyOwnMessage);
            }
        } else if (message.messageType === MessageType.clientMessage && message.readTimestamp === null) {
            props.increaseChatUnreadMessagesCount(message.chatId);
        }
        if (message.messageType != MessageType.systemMessage)
        {
            props.playNewMessageSound(newMessageSoundPlayer, message);
        }
        props.pushNewMessageNotification(message);
    };

    const onMessagePreview = (messagePreview: IMessagePreview) => {
        props.updateMessagePreviewToState(messagePreview);
        const hasText = messagePreview.text.length > 0;
    
        const wasActive = isPreviewActiveRef.current;

        if (hasText && !wasActive && selectedChatId === messagePreview.chatId) {
            _scrollDown();
        }

        isPreviewActiveRef.current = hasText;

        return;
    };

    const onOperatorStatus = (status: OperatorStatusType) => {
        console.log(`oldStatus ${props.operatorStatus}, newStatus ${status}`);
        props.changeOperatorStatus(status, onOperatorStatusChanged);
    };

    const onUpdateMessage = (message: IMessageItem) => {
        props.updateChatMessage(message);
    };

    const onUpdateOperatorStatus = (status: IOperatorStatusViewModel) => {
        props.updateOperatorStatusInState(status);
    };

    const onLogoutOperator = (operatorId: number, logoutBy: string) => {
        props.onLoggedOut(logoutBy, operatorId);
    };

    const onClientWentOffline = (
        messengerExternalId: string,
        channelToken: string
    ) => {
        props.updateIsContactOnline(false, channelToken, messengerExternalId);
    };

    const onClientWentOnline = (
        messengerExternalId: string,
        channelToken: string
    ) => {
        props.updateIsContactOnline(true, channelToken, messengerExternalId);
    };

    const _scrollDown = (isMyOwnMessage: boolean = false): void => {
        setTimeout(() => {
            const chatBody = document.getElementById("chat-body");

            //Scroll down if the user is within a certain distance from the bottom of the chat,
            //specifically if the bottom of the scroll is no more than two chat body heights away from the scroll bottom.
            if (!chatBody) return;

            const lastLi = chatBody.querySelector("ul > li:last-child") as HTMLElement | null;
            if (!lastLi) return;

            const targetDiv = lastLi.querySelector("div") as HTMLElement | null;
            if (!targetDiv) return;

            const nestedDiv = targetDiv.querySelector("div") as HTMLElement | null;
            if (!nestedDiv) return;

            const imageElement = nestedDiv.querySelector("div div a img") as HTMLImageElement | null;

            const scrollThreshold = chatBody.offsetHeight * 1.5;
            const distanceToBottom = chatBody.scrollHeight - chatBody.scrollTop;

            const isThisTabFocused = document.visibilityState === "visible" && document.hasFocus();

            const isNeedToScroll = distanceToBottom <= scrollThreshold || (isMyOwnMessage && isThisTabFocused);

            const waitForImage = (img: HTMLImageElement) => {
                return new Promise<void>((resolve) => {
                    if (img.complete && img.naturalHeight !== 0) {
                        resolve();
                    } else {
                        img.onload = () => resolve();
                        img.onerror = () => resolve();
                    }
                });
            };

            const doScroll = () => {
                if (isNeedToScroll) {
                    chatBody.scrollTo({
                        top: chatBody.scrollHeight,
                        behavior: "smooth"
                    });
                }
            };

            if (imageElement) {
                waitForImage(imageElement).then(doScroll);
            } else {
                doScroll();
            }
        }, 50);
    };

    const _mapChats = (): React.ReactElement<{}> => {
        return (
            <React.Fragment>
                <div className="chats-panel__chat-list">
                    {props.chatItems.map((chat, index) =>
                        chat.fromHistory === false &&
                        chat.isClosed === false ? (
                            <button
                                id={"chat-item__chatid-" + chat.chatId}
                                className={"chat-item-active-element"}
                                key={index}
                                onClick={() => onChatSelected(chat, true)}
                                tabIndex={0}
                                onKeyUp={(e) => {
                                    if (e.key === "Enter") {
                                        onChatSelected(chat, true);
                                    }
                                }}
                            >
                                <ChatItem
                                    chatItem={chat}
                                    isSelected={
                                        chat.chatId === props.selectedChatId
                                    }
                                    operatorId={props.operatorId}
                                />
                            </button>
                        ) : null
                    )}
                </div>
            </React.Fragment>
        );
    };

    const _mapPauseCategories = () => {
        return (
            <React.Fragment>
                {props.pauseCategories && props.pauseCategories.length > 0 ? (
                    <optgroup
                        className={"chat-panel__operator-status-afk"}
                        label={t("common.pause")}
                    >
                        {props.pauseCategories.map((item, index) => {
                            return (
                                <option
                                    key={index}
                                    className={
                                        "chat-panel__operator-status-afk"
                                    }
                                    value={FIRST_PAUSE_CATEGORY_ID + index}
                                >
                                    {item}
                                </option>
                            );
                        })}
                    </optgroup>
                ) : (
                    <option
                        className={"chat-panel__operator-status-afk"}
                        value={OperatorStatusType.Pause}
                    >
                        {t("common.pause")}
                    </option>
                )}
            </React.Fragment>
        );
    };

    const onChatSelected = (chat: IChatItem, needToClearHistory = false) => {
        toggleProfileDropDownVisibility(false);
        if (
            props.selectedChatId === chat.chatId &&
            props.isChatVisible === true
        )
            return;
        setChatVisibilityToAll(true);
        props.loadChatToWidget(chat.chatId);
        activateChatsTabIfNeed(chat);
    };

    const activateChatsTabIfNeed = (chat: IChatItem) => {
        if (
            chat.operatorId === props.operatorId &&
            props.getChatsMode !== GetChatsMode.my
        ) {
            props.onGetChatsModeChanged(GetChatsMode.my, true);
        }
        if (
            chat.invitedOperatorsId.includes(props.operatorId) &&
            props.getChatsMode !== GetChatsMode.withMe
        ) {
            props.onGetChatsModeChanged(GetChatsMode.withMe, true);
        }
    };

    const setChatVisibilityToAll = (value: boolean) => {
        props.setChatVisibility(value);
        props.setChatVisibilityToState(value);
    };

    const toggleProfileDropDownVisibility = (value: boolean) => {
        props.toggleProfileDropDownVisibility(value);
    };

    const onLogout = (logoutMessage: string) => {
        sessionStorage.removeItem("user");
        redirectToLogin(logoutMessage ? logoutMessage : "");
    };

    const selectedOperatorStatus = props.pauseCategories && props.pauseCategories.length === 0
        && props.operatorStatus >= FIRST_PAUSE_CATEGORY_ID
        ? OperatorStatusType.Pause
        : props.operatorStatus;

    return (
        <div className="chats-panel__container">
            <Avatar
                tabIndex={0}
                alt=""
                className="chats-panel__logout"
                id="logout"
                src={operatorAvatarUrl}
                onClick={() =>
                    toggleProfileDropDownVisibility(
                        !props.isProfileDropdownVisible
                    )
                }
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        toggleProfileDropDownVisibility(
                            !props.isProfileDropdownVisible
                        );
                    }
                }}
            >
                <ContactIcon style={{ color: "#26a69a" }} />
            </Avatar>
            <div
                className="chat-panel__operator-name"
                title={props.operatorName}
                data-testid="operator-name"
            >
                {props.operatorName}
            </div>
            {props.operatorId !== 0 && props.operatorId && (
                <div className={"chat-panel__operator-status-container"}>
                    <select
                        data-testid="operator-status-select"
                        disabled={props.reconnectingStatus}
                        tabIndex={0}
                        onChange={(e) => {
                            const newStatus = +e.target.value;
                            if (newStatus === OperatorStatusType.Offline) {
                                offlineHandled = true;
                                props.getChats();
                                const openedChats = props.chatItems.filter(c => !c.isClosed);
                                if (openedChats.length > 0) {
                                    const confirmLogout = window.confirm(t('common.offlineStatusConfirmation'));
                                    if (!confirmLogout) {
                                        offlineHandled = false;
                                        return;
                                    }
                                }
                            }
                            props.changeOperatorStatus(newStatus, onOperatorStatusChanged, true);
                        }}
                        value={selectedOperatorStatus}
                        className={
                            "chat-panel__selected-status-" +
                            (props.operatorStatus ===
                                OperatorStatusType.Online ||
                            props.operatorStatus === OperatorStatusType.Offline
                                ? props.operatorStatus
                                : OperatorStatusType.Pause)
                        }
                    >
                        <option
                            className={"chat-panel__operator-status-online"}
                            value={OperatorStatusType.Online}
                        >
                            {t("common.online")}
                        </option>
                        {_mapPauseCategories()}
                        <option
                            className={"chat-panel__operator-status-offline"}
                            value={OperatorStatusType.Offline}
                        >
                            {t("common.offline")}
                        </option>
                    </select>
                    {props.reconnectingStatus && <Loader />}
                </div>
            )}
            <div className="chat-panel__chat-items-container" data-testid="chats-list">
                {_mapChats()}
            </div>
            {props.isProfileDropdownVisible && (
                <ProfileDropdown
                    onLogout={onLogout}
                    operatorChannels={props.operatorChannels}
                    operatorsStatusList={props.operatorsStatusList}
                    pauseCategories={props.pauseCategories}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state: IApplicationState, externalProps: any) => {
    return {
        chatItems: chatItemsSelector(state),
        isChatVisible: externalProps.isChatVisible,
        isProfileDropdownVisible: externalProps.isProfileDropdownVisible,
        toggleProfileDropDownVisibility:
            externalProps.toggleProfileDropDownVisibility,
        operatorStatus: operatorStatusSelector(state),
        pauseCategories: pauseCategoriesSelector(state),
        reconnectingStatus: reconnectingStatusSelector(state),
        hubConnection: hubConnectionSelector(state),
        selectedChatId: selectedChatIdSelector(state),
        selectedChatMessages: messegesSelector(state),
        operatorName: operatorNameSelector(state),
        operatorAvatarId: operatorAvatarIdSelector(state),
        operatorId: operatorIdSelector(state),
        operatorChannels: operatorChannelsSelector(state),
        setChatVisibility: externalProps.setChatVisibility,
        getChatsMode: state.chatsState.chatsList.getChatsMode,
        operatorsStatusList: operatorsStatusListSelector(state),
    };
};

const mapDispatchToProps = (dispatch: any): IChatPanelProps => {
    return {
        loadChatToWidget: (chatId: number) =>
            dispatch(loadChatToWidget(chatId)),
        getChats: (): AppThunkAction<GetOpenedOperatorChatsAction> => {
            return dispatch(getChats());
        },
        changeOperatorStatus: (
            status: OperatorStatusType,
            callback: CallableFunction,
            isFromUI: boolean = false
        ): AppThunkAction<ChangeOperatorStatusAction> => {
            return dispatch(changeOperatorStatus(status, callback, isFromUI));
        },
        changeReconnectingStatus: (
            status: boolean
        ): AppThunkAction<ChangeReconnectingStatusAction> => {
            return dispatch(changeReconnectingStatus(status));
        },
        startOperatorLostConnectionHandler: (): AppThunkAction<void> => {
            return dispatch(startOperatorLostConnectionHandler());
        },
        UpdateIncomingChatInState: (
            chat
        ): AppThunkAction<UpdateIncomingChatInStateAction> => {
            return dispatch(UpdateIncomingChatInState(chat));
        },
        updateMessagePreviewToState: (message: IMessagePreview) => {
            return dispatch(updateMessagePreviewToState(message));
        },
        addNewMessageToState: (
            message: IMessageItem
        ): AppThunkAction<AddNewMessageToStateAction> => {
            return dispatch(addNewMessageToState(message));
        },
        updateChatMessage: (
            message: IMessageItem
        ): AppThunkAction<UpdateMessageInStateAction> => {
            return dispatch(updateChatMessage(message));
        },
        getOperatorInfo: (): AppThunkAction<GetOperatorInfoAction> => {
            return dispatch(getOperatorInfo());
        },
        getOperatorChannels: (): AppThunkAction<GetOperatorChannelsAction> => {
            return dispatch(getOperatorChannels());
        },
        getOperatorStatusList:
            (): AppThunkAction<GetOperatorStatusListAction> => {
                return dispatch(getOperatorStatusList());
            },
        getAvailableContactTags:
            (): AppThunkAction<GetAvailableContactTagsAction> => {
                return dispatch(getAvailableContactTags());
            },
        updateOperatorStatusInState: (
            opeatorStatus: IOperatorStatusViewModel
        ): AppThunkAction<UpdateOperatorStatusInStateAction> => {
            return dispatch(updateOperatorStatusInState(opeatorStatus));
        },
        getChannelInfo: (
            channelId: number
        ): AppThunkAction<GetChannelInfoAction> => {
            return dispatch(getChannelInfo(channelId));
        },
        increaseChatUnreadMessagesCount: (
            chatId: number
        ): AppThunkAction<IncreaseChatUnreadMessagesCountAction> => {
            return dispatch(increaseChatUnreadMessagesCount(chatId));
        },
        setChatVisibilityToState: (value: boolean) => {
            return dispatch(setChatVisibilityToState(value));
        },
        onGetChatsModeChanged: (
            mode: GetChatsMode,
            needGetChats: boolean = false
        ) => {
            return dispatch(onGetChatsModeChanged(mode, needGetChats));
        },
        updateChatsInChatPanel: (chats: IChatItem[]) =>
            dispatch(updateChatsInChatPanel(chats)),
        updateChats: (chats: IChat[]) => dispatch(updateChats(chats)),
        updateChatHistory: (chats: IChat[]) => dispatch(updateChatHistory(chats)),
        hideChats: (chatsIds: number[]) => dispatch(hideChats(chatsIds)),
        updateIsContactOnline: (
            status: boolean,
            channelToken: string,
            messengerExternalId: string
        ) =>
            dispatch(
                updateIsContactOnline(status, channelToken, messengerExternalId)
            ),
        playNewMessageSound: (
            soundPlayer: HTMLAudioElement,
            message: IMessageItem
        ) => dispatch(playNewMessageSound(soundPlayer, message)),
        pushNewMessageNotification: (message: IMessageItem) =>
            dispatch(pushNewMessageNotification(message)),
        onLoggedOut: (logoutBy: string, operatorId: number) =>
            dispatch(onLoggedOut(logoutBy, operatorId)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatsPanel);
